import {useMemo} from 'react';
import {useHalState} from '@synako/haligator';
import {PrixHistoriqueArticle} from 'api-types/ffconnect';
import {useClient} from '@/haligator-factory';

export type PrixDUneLivraison = NonNullable<
  PrixHistoriqueArticle['tolede']['stats_par_livraison'][number]['prix']
>;

/**
 * Hook : prix unitaires de l'historique d'achat d'un article, par date de livraison.
 *
 * Le lien `prix-historique-article` du client n'est présent que pour un commercial
 * (`voir-prix-historique`) dont la société commerciale du client autorise les prix :
 * sans lui, `peutVoirPrix` vaut false et rien n'est chargé.
 * `charger` réserve l'appel à l'ouverture du tooltip ou de la modale.
 */
export function usePrixHistoriqueArticle(
  code_article: string | undefined,
  charger: boolean,
): {
  peutVoirPrix: boolean;
  /** `undefined` tant que les prix ne sont pas chargés, vide si la requête a échoué. */
  prixParDate: Map<string, PrixDUneLivraison> | undefined;
} {
  const clientState = useHalState(useClient());
  const peutVoirPrix =
    clientState.state?.hasOneLink('prix-historique-article') ?? false;

  const {data, error} = useHalState(
    charger && peutVoirPrix && code_article && clientState.state
      ? clientState.state.follow('prix-historique-article', {code_article})
      : undefined,
  );

  // useHalState garde la réponse précédente quand le lien change : on ne la lit
  // que si elle porte bien sur l'article affiché.
  const dataDeLArticle = data?.code_article === code_article ? data : undefined;
  const prixParDate = useMemo(() => {
    // En erreur (403 si la coche de la société a été retirée depuis le chargement du client,
    // panne…) : aucun prix plutôt qu'un squelette sans fin
    if (error) return new Map<string, PrixDUneLivraison>();
    return (
      dataDeLArticle &&
      new Map(
        dataDeLArticle.tolede.stats_par_livraison.map((stat) => [
          stat.livraison_du,
          stat.prix ?? [],
        ]),
      )
    );
  }, [dataDeLArticle, error]);

  return {peutVoirPrix, prixParDate};
}
